import { createContext, useContext, useState, type ReactNode } from 'react';

type Language = 'zh' | 'en';

interface Translations {
    navbar: {
        logo: string;
        lang: string;
    };
    hero: {
        newBadge: string;
        newBadgeText: string;
        titleLine1: string;
        titleLine2: string;
        titleLine3: string; // Used for split line in EN or extra text
        subtitle: string;
        description: string;
        cta: string;
        videoAlt: string;
    };
    features: {
        badge: string;
        titleLine1: string;
        titleLine2: string;
        steps: {
            id: number;
            step: string;
            title: string;
            subtitle: string;
            description: string;
        }[];
        learnMore: string;
    };
    trustedBy: {
        title: string;
    };
    faq: {
        badge: string;
        title: string;
        description: string;
        items: { question: string; answer: string }[];
    };
    cta: {
        titleLine1: string;
        titleLine2: string;
        subtitle: string;
        button: string;
        features: string[];
    };
    footer: {
        slogan: string;
        subscribeTitle: string;
        subscribePlaceholder: string;
        subscribeNamePlaceholder: string;
        subscribeRoleLabel: string;
        subscribeRoleOptions: { value: string; label: string }[];
        subscribeOtherRolePlaceholder: string;
        subscribeInterestsLabel: string;
        subscribeInterests: { value: string; label: string }[];
        subscribeButton: string;
        subscribeSuccess: string;
        subscribeError: string;
        subscribeAlready: string;
        product: string;
        company: string;
        resources: string;
        legal: string;
        rights: string;
        productLinks: { label: string; href: string }[];
        companyLinks: { label: string; href: string }[];
        resourcesLinks: { label: string; href: string }[];
        legalLinks: { label: string; href: string }[];
    };
}

const translations: Record<Language, Translations> = {
    zh: {
        navbar: {
            logo: 'DesignSolution',
            lang: 'CN',
        },
        hero: {
            newBadge: 'NEW',
            newBadgeText: 'DesignSolution v2.0 已发布',
            titleLine1: '智能构筑无限可能',
            titleLine2: '设计流动超越边界',
            titleLine3: '', // Merged in design logic
            subtitle: '', // Using the second part of the slogan as subtitle or part of title based on request.
            // Request: "智能构筑无限可能，设计流动超越边界"
            // I will fit this into the specific layout of the Hero component.
            description: 'DesignSolution 是专注于北美独立屋的智能设计平台，我们运用AI算法将建筑设计与空间美学深度融合，并用混合建造的方式重塑传统住宅，为您提供从规划到落成的全过程体验。',
            cta: '立即开始体验',
            videoAlt: 'DesignSolution AI户型设计平台演示',
        },
        features: {
            badge: '工作流程',
            titleLine1: '从概念到现实',
            titleLine2: '仅需六步',
            steps: [
                {
                    id: 1,
                    step: '01',
                    title: '定位与智能合规',
                    subtitle: 'LOCATION & COMPLIANCE',
                    description: '接入真实地理信息系统，获取精准的环境数据（地形、光照、周边配套）。内置北美建筑规范数据库，自动审核退红线、容积率、建筑高度等关键指标，确保设计从一开始就符合法规要求。',
                },
                {
                    id: 2,
                    step: '02',
                    title: '自定义开发规划',
                    subtitle: 'DEVELOPMENT PLANNING',
                    description: '使用直观的绘图工具，轻松勾勒地块轮廓与开发边界。系统自动识别尺寸与面积，支持参数化设置建筑密度、绿化率等规划指标，完美适配混合建造及装配式建筑的施工图纸标准。',
                },
                {
                    id: 3,
                    step: '03',
                    title: '自定义居住空间',
                    subtitle: 'LIVING SPACE',
                    description: '根据家庭需求自由配置房间数量、功能分区与空间比例。支持预设模板或完全自定义，从卧室、浴室到车库、地下室，打造真正符合您生活方式的理想居所。',
                },
                {
                    id: 4,
                    step: '04',
                    title: '生成与智能交互',
                    subtitle: 'AI GENERATION',
                    description: '告别繁琐的方案草图绘制。通过参数化设置或自然语言对话，AI 瞬间生成数百种住宅户型方案。支持通过对话实时微调布局，并可导出 DXF 等专业格式。',
                },
                {
                    id: 5,
                    step: '05',
                    title: '外观与建筑漫游',
                    subtitle: 'EXTERIOR & WALKTHROUGH',
                    description: '一键将二维平面转化为高保真 3D 模型。支持自由漫游体验空间感，并可智能生成建筑外观效果图，涵盖现代、传统、农舍等多种风格，所见即所得。',
                },
                {
                    id: 6,
                    step: '06',
                    title: 'AI建造数据分析',
                    subtitle: 'CONSTRUCTION ANALYSIS',
                    description: '提供建筑荷载、结构强度、材料用量及成本估算等多维分析。深度融合传统风水学，智能分析气场流向与空间布局吉凶，助您打造科学安全与人文和谐兼备的理想居所。',
                },
            ],
            learnMore: '了解更多',
        },
        trustedBy: {
            title: '受到众多知名房企信赖',
        },
        faq: {
            badge: '常见问题',
            title: '了解',
            description: '快速了解 DesignSolution',
            items: [
                {
                    question: 'DesignSolution 怎样用 AI 生成符合当地风格的独立屋设计的？',
                    answer: '我们的 AI 引擎深度学习了北美各地区数十万套独立屋案例，涵盖 Craftsman、Ranch、Colonial、Contemporary 等主流风格。系统会根据您选择的地区自动匹配当地流行的建筑风格、屋顶形式、立面比例和材料偏好，确保生成的设计既符合当地审美，又满足 HOA 和市政规范要求。',
                },
                {
                    question: '设计支持的混合建造在当地有可以推荐的合规工程公司吗？',
                    answer: '是的，DesignSolution 与北美多地区的持牌总承包商（GC）、结构工程师和专业分包商建立了合作网络。我们可根据您的项目地点，推荐经过资质审核的合规工程公司，涵盖传统框架、模块化预制、3D 打印等多种建造方式，确保从设计到施工的无缝衔接。',
                },
                {
                    question: '如何获取跨境的深化团队、设计买手和供应链资讯？',
                    answer: 'DesignSolution 提供完整的跨境资源对接服务。我们拥有专业的深化设计团队可提供施工图深化服务，同时与多家建材供应商、家具买手和物流服务商建立合作，为您提供从设计深化、材料采购到国际物流的一站式解决方案，显著降低跨境建造成本。',
                },
                {
                    question: '建筑法规的检查功能与当地职能部门的审查意见是否一致？',
                    answer: '我们的法规检查引擎基于 IRC（国际住宅规范）及各州/市的补充条款构建，覆盖退红线、容积率、建筑高度、防火间距等核心指标。虽然系统检查结果具有高度参考价值，但最终审批以当地建筑部门（Building Department）的正式意见为准。建议在正式报建前，使用我们的"预审报告"功能与当地官员进行预沟通。',
                },
                {
                    question: 'DesignSolution 适合小建商和业主使用吗？',
                    answer: '非常适合！这正是我们的核心用户群体。对于小型建商，DesignSolution 可以大幅缩短方案设计周期，快速响应客户需求，提升项目周转效率；对于自建房业主，平台让专业设计变得触手可及，您可以在没有建筑背景的情况下，轻松完成从概念到施工图的全流程，并与承包商进行高效沟通。',
                },
            ],
        },
        cta: {
            titleLine1: '立即开启您的',
            titleLine2: '设计之旅',
            subtitle: '快速设计，高效规划，让创意无限延展',
            button: '加入等候名单',
            features: ['AI 智能生成', '无限设计方案', '实时协作', '专业导出'],
        },
        footer: {
            slogan: 'DesignSolution 利用先进的生成式 AI 技术，为您提供从智能选址、户型生成到 3D 渲染的一站式建筑设计解决方案。我们融合传统风水智慧与现代建筑规范，突破传统住宅设计局限，让每个人都能轻松规划理想居所，开启智能设计新时代。',
            subscribeTitle: '加入等候名单',
            subscribePlaceholder: '输入您的邮箱 *',
            subscribeNamePlaceholder: '输入您的姓名（可选）',
            subscribeRoleLabel: '您的角色',
            subscribeRoleOptions: [
                { value: '', label: '请选择...' },
                { value: 'developer', label: '房地产开发商' },
                { value: 'architect', label: '建筑师/设计师' },
                { value: 'homeowner', label: '业主/自建房' },
                { value: 'contractor', label: '承包商/建筑商' },
                { value: 'other', label: '其他' },
            ],
            subscribeOtherRolePlaceholder: '请描述您的角色',
            subscribeInterestsLabel: '感兴趣的功能',
            subscribeInterests: [
                { value: 'ai-generation', label: 'AI户型生成' },
                { value: '3d-rendering', label: '3D渲染漫游' },
                { value: 'compliance', label: '规范合规检查' },
                { value: 'cost-estimation', label: '成本估算' },
                { value: 'cross-border', label: '跨境资源对接' },
                { value: 'hybrid-construction', label: '混合建造方式' },
            ],
            subscribeButton: '提交订阅',
            subscribeSuccess: '订阅成功！感谢您的关注。',
            subscribeError: '订阅失败，请稍后重试',
            subscribeAlready: '您已经订阅过了',
            product: '产品',
            company: '公司',
            resources: '资源',
            legal: '法律',
            rights: 'Copyright © 2025 DASON AI Pte. Ltd. All Rights Reserved',
            productLinks: [
                { label: '功能介绍', href: '#features' },
                { label: '定价方案', href: '#' },
                { label: '更新日志', href: '#' },
                { label: 'API 文档', href: '#' },
            ],
            companyLinks: [
                { label: '关于我们', href: '#' },
                { label: '博客', href: '#' },
                { label: '加入我们', href: '#' },
                { label: '联系我们', href: '#' },
            ],
            resourcesLinks: [
                { label: '帮助中心', href: '#' },
                { label: '使用教程', href: '#' },
                { label: '社区论坛', href: '#' },
                { label: '设计案例', href: '#' },
            ],
            legalLinks: [
                { label: '隐私政策', href: '#' },
                { label: '服务条款', href: '#' },
                { label: 'Cookie 政策', href: '#' },
            ],
        },
    },
    en: {
        navbar: {
            logo: 'DesignSolution',
            lang: 'EN',
        },
        hero: {
            newBadge: 'NEW',
            newBadgeText: 'DesignSolution v2.0 is live',
            titleLine1: 'Smart Build',
            titleLine2: 'Flow Beyond',
            titleLine3: '',
            subtitle: '',
            description: 'DesignSolution is an intelligent design platform dedicated to North American single-family homes. We utilize AI algorithms to deeply integrate architectural design with spatial aesthetics, and reshape traditional housing through hybrid construction, offering you an end-to-end experience from planning to completion.',
            cta: 'Start Experience Now',
            videoAlt: 'DesignSolution AI Design Platform Demo',
        },
        features: {
            badge: 'WORKFLOW',
            titleLine1: 'From Concept to Reality',
            titleLine2: 'In Just 6 Steps',
            steps: [
                {
                    id: 1,
                    step: '01',
                    title: 'Location & Smart Compliance',
                    subtitle: 'LOCATION & COMPLIANCE',
                    description: 'Access real GIS data for precise environment analysis (terrain, lighting, nearby amenities). Built-in North American building codes automatically check setbacks, FAR, building height and other key metrics, ensuring designs comply with regulations from the start.',
                },
                {
                    id: 2,
                    step: '02',
                    title: 'Custom Development Planning',
                    subtitle: 'DEVELOPMENT PLANNING',
                    description: 'Use intuitive drawing tools to sketch plot outlines and development boundaries. The system automatically identifies dimensions and area, supports parametric settings for building density, green space ratio and other planning indicators, fully adapted to hybrid and prefabricated construction standards.',
                },
                {
                    id: 3,
                    step: '03',
                    title: 'Custom Living Space',
                    subtitle: 'LIVING SPACE',
                    description: 'Freely configure room count, functional zones and space proportions based on family needs. Supports preset templates or full customization, from bedrooms, bathrooms to garages and basements, creating your ideal home that truly matches your lifestyle.',
                },
                {
                    id: 4,
                    step: '04',
                    title: 'Generation & Smart Interaction',
                    subtitle: 'AI GENERATION',
                    description: 'Stop spending months on schematic designs. Input constraints via parameters or natural language. Generate hundreds of floorplan variations instantly, and fine-tune layouts through conversation. Supports DXF export.',
                },
                {
                    id: 5,
                    step: '05',
                    title: 'Exterior & Building Walkthrough',
                    subtitle: 'EXTERIOR & WALKTHROUGH',
                    description: 'One-click conversion to high-fidelity 3D models. Experience spatial depth through free roaming, and smart generate building exteriors covering Modern, Traditional, Farmhouse and more styles. What you see is what you get.',
                },
                {
                    id: 6,
                    step: '06',
                    title: 'AI Construction Analytics',
                    subtitle: 'CONSTRUCTION ANALYSIS',
                    description: 'Provides multi-dimensional analysis on building loads, structural strength, material quantities and cost estimation. Deeply integrates traditional Feng Shui to analyze Qi flow and spatial harmony, creating an ideal home combining science with culture.',
                },
            ],
            learnMore: 'Learn More',
        },
        trustedBy: {
            title: 'Trusted by Leading Real Estate Developers',
        },
        faq: {
            badge: 'FAQ',
            title: 'Common',
            description: 'Quickly understand DesignSolution',
            items: [
                {
                    question: 'How does DesignSolution use AI to generate locally-styled single-family home designs?',
                    answer: 'Our AI engine has deep-learned hundreds of thousands of single-family homes across North American regions, covering popular styles like Craftsman, Ranch, Colonial, and Contemporary. The system automatically matches local architectural styles, roof forms, facade proportions, and material preferences based on your selected region, ensuring designs meet both local aesthetics and HOA/municipal code requirements.',
                },
                {
                    question: 'Can you recommend licensed contractors for hybrid construction in my area?',
                    answer: 'Yes, DesignSolution has established a partnership network with licensed General Contractors (GC), structural engineers, and specialized subcontractors across multiple North American regions. Based on your project location, we can recommend vetted, compliant construction companies covering traditional framing, modular prefab, 3D printing, and other building methods, ensuring seamless transition from design to construction.',
                },
                {
                    question: 'How can I access cross-border design teams, procurement agents, and supply chain resources?',
                    answer: 'DesignSolution offers comprehensive cross-border resource connection services. We have professional teams for construction document development, and partnerships with building material suppliers, furniture buyers, and logistics providers to offer one-stop solutions from design development, material procurement to international logistics, significantly reducing cross-border construction costs.',
                },
                {
                    question: 'Does the code compliance check align with local building department reviews?',
                    answer: 'Our compliance engine is built on IRC (International Residential Code) and state/municipal amendments, covering setbacks, FAR, building height, fire separation, and other core metrics. While our system checks provide highly reliable reference, final approval is subject to official decisions from your local Building Department. We recommend using our "Pre-Review Report" feature to pre-communicate with local officials before formal permit submission.',
                },
                {
                    question: 'Is DesignSolution suitable for small builders and homeowners?',
                    answer: 'Absolutely! This is our core user base. For small builders, DesignSolution dramatically shortens design cycles, enables quick response to client needs, and improves project turnover. For self-build homeowners, the platform makes professional design accessible—you can complete the entire process from concept to construction drawings without architectural background, and communicate efficiently with contractors.',
                },
            ],
        },
        cta: {
            titleLine1: 'Start Your',
            titleLine2: 'Design Journey',
            subtitle: 'Fast design, efficient planning, unlimited creativity',
            button: 'Join Waitlist',
            features: ['AI Generation', 'Unlimited Plans', 'Real-time Collab', 'Pro Export'],
        },
        footer: {
            slogan: 'DesignSolution leverages advanced generative AI technology to provide a one-stop architectural design solution from smart site selection, floor plan generation to 3D rendering. We integrate traditional Feng Shui wisdom with modern building codes, breaking through the limitations of traditional residential design, making it easy for everyone to plan their ideal home and usher in a new era of intelligent design.',
            subscribeTitle: 'Join the Waitlist',
            subscribePlaceholder: 'Enter your email *',
            subscribeNamePlaceholder: 'Enter your name (optional)',
            subscribeRoleLabel: 'Your Role',
            subscribeRoleOptions: [
                { value: '', label: 'Select...' },
                { value: 'developer', label: 'Real Estate Developer' },
                { value: 'architect', label: 'Architect / Designer' },
                { value: 'homeowner', label: 'Homeowner / Self-Builder' },
                { value: 'contractor', label: 'Contractor / Builder' },
                { value: 'other', label: 'Other' },
            ],
            subscribeOtherRolePlaceholder: 'Please describe your role',
            subscribeInterestsLabel: 'Interested Features',
            subscribeInterests: [
                { value: 'ai-generation', label: 'AI Floor Plan Generation' },
                { value: '3d-rendering', label: '3D Rendering & Walkthrough' },
                { value: 'compliance', label: 'Code Compliance Check' },
                { value: 'cost-estimation', label: 'Cost Estimation' },
                { value: 'cross-border', label: 'Cross-border Resources' },
                { value: 'hybrid-construction', label: 'Hybrid Construction' },
            ],
            subscribeButton: 'Subscribe',
            subscribeSuccess: 'Subscribed successfully! Thank you.',
            subscribeError: 'Subscription failed, please try again',
            subscribeAlready: 'You are already subscribed',
            product: 'Product',
            company: 'Company',
            resources: 'Resources',
            legal: 'Legal',
            rights: 'Copyright © 2025 DASON AI Pte. Ltd. All Rights Reserved',
            productLinks: [
                { label: 'Features', href: '#features' },
                { label: 'Pricing', href: '#' },
                { label: 'Changelog', href: '#' },
                { label: 'API Docs', href: '#' },
            ],
            companyLinks: [
                { label: 'About Us', href: '#' },
                { label: 'Blog', href: '#' },
                { label: 'Careers', href: '#' },
                { label: 'Contact', href: '#' },
            ],
            resourcesLinks: [
                { label: 'Help Center', href: '#' },
                { label: 'Tutorials', href: '#' },
                { label: 'Community', href: '#' },
                { label: 'Showcase', href: '#' },
            ],
            legalLinks: [
                { label: 'Privacy', href: '#' },
                { label: 'Terms', href: '#' },
                { label: 'Cookies', href: '#' },
            ],
        },
    },
};

interface LanguageContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: Translations;
    toggleLanguage: () => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
    const [language, setLanguage] = useState<Language>('zh');

    const toggleLanguage = () => {
        setLanguage((prev) => (prev === 'zh' ? 'en' : 'zh'));
    };

    return (
        <LanguageContext.Provider
            value={{
                language,
                setLanguage,
                t: translations[language],
                toggleLanguage,
            }}
        >
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguage() {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
}
